import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  HeartPulse, Heart, Droplet, Shield, Bone, Brain, Leaf, Compass,
  Loader2, ArrowRight, Sparkles, Smile,
} from "lucide-react";
import { useAuth } from "@/App";

/**
 * HealthHubCompass — visual map of every specialised intelligence
 * module available to the user. Rendered on the main Health
 * Intelligence page. Shows colour-coded status per module and links
 * to launched modules directly.
 */

const ICONS = {
  "heart-pulse": HeartPulse,
  "heart":       Heart,
  "droplet":     Droplet,
  "shield":      Shield,
  "bone":        Bone,
  "brain":       Brain,
  "leaf":        Leaf,
  "tooth":       Smile,
};

const STATUS_COLORS = {
  green:    { ring: "#22C55E", bg: "bg-emerald-100 dark:bg-emerald-500/15", text: "text-emerald-700 dark:text-emerald-300" },
  yellow:   { ring: "#EAB308", bg: "bg-amber-100 dark:bg-amber-500/15",     text: "text-amber-700 dark:text-amber-300"   },
  orange:   { ring: "#F97316", bg: "bg-orange-100 dark:bg-orange-500/15",   text: "text-orange-700 dark:text-orange-300" },
  red:      { ring: "#EF4444", bg: "bg-rose-100 dark:bg-rose-500/15",       text: "text-rose-700 dark:text-rose-300"     },
  inactive: { ring: "#9CA3AF", bg: "bg-gray-100 dark:bg-white/5",           text: "text-gray-600 dark:text-gray-400"     },
};

const T = {
  bg: {
    title: "Health Hub Compass",
    subtitle: "Преглед на специализираните intelligence модули на платформата — навигирайте към всеки от тях.",
    soon: "Очаквайте",
    open: "Отвори",
    launched: "налични",
    of: "от",
    statusLabels: { green: "Защитен", yellow: "Среден", orange: "Внимание", red: "Висок натиск" },
  },
  en: {
    title: "Health Hub Compass",
    subtitle: "Overview of the platform's specialised intelligence modules — navigate to any of them.",
    soon: "Coming soon",
    open: "Open",
    launched: "available",
    of: "of",
    statusLabels: { green: "Protective", yellow: "Average", orange: "Caution", red: "High burden" },
  },
  es: {
    title: "Health Hub Compass",
    subtitle: "Resumen de los módulos de inteligencia especializados — navegue a cualquiera de ellos.",
    soon: "Próximamente",
    open: "Abrir",
    launched: "disponibles",
    of: "de",
    statusLabels: { green: "Protector", yellow: "Medio", orange: "Atención", red: "Alta carga" },
  },
};

export default function HealthHubCompass({ lang = "bg", isDark = false }) {
  const { API, user } = useAuth();
  const t = T[lang] || T.bg;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCompass = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/health-hub/compass`);
      setData(data);
    } catch (e) {
      // silent — page will show without compass
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (user) fetchCompass();
  }, [user, fetchCompass]);

  if (loading) {
    return (
      <div className="py-6 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-50" />
      </div>
    );
  }
  if (!data?.modules?.length) return null;

  return (
    <div className="my-6" data-testid="health-hub-compass">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Compass className="w-5 h-5 text-pink-500" />
          <h2 className="text-lg sm:text-xl font-bold">{t.title}</h2>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
          isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"
        }`}>
          {data.summary.total_launched} {t.of} {data.summary.total_available} {t.launched}
        </span>
      </div>
      <p className="text-xs opacity-60 mb-4">{t.subtitle}</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {data.modules.map((m, i) => (
          <ModuleCard
            key={m.id}
            module={m}
            index={i}
            t={t}
            isDark={isDark}
            lang={lang}
          />
        ))}
      </div>

      <p className="text-[11px] opacity-50 mt-3 italic">{data.disclaimer}</p>
    </div>
  );
}

function ModuleCard({ module, index, t, isDark, lang }) {
  const Icon = ICONS[module.icon] || Sparkles;
  const statusKey = module.launched ? module.status : "inactive";
  const colors = STATUS_COLORS[statusKey] || STATUS_COLORS.inactive;
  const name = module[`name_${lang}`] || module.name_en;
  const summary = module[`summary_${lang}`] || module.summary_en;
  const isLink = module.launched && module.route !== "#";

  const Inner = (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      whileHover={isLink ? { scale: 1.02 } : {}}
      className={`relative p-4 rounded-2xl border-2 transition-all ${colors.bg} ${
        module.launched ? "border-transparent hover:border-pink-300/40" : "border-dashed border-gray-300/30"
      } ${!isLink ? "opacity-70" : ""}`}
      style={isLink ? { boxShadow: `0 0 0 0 ${colors.ring}40` } : {}}
      data-testid={`hub-module-${module.id}`}
    >
      {/* Top row: icon + score */}
      <div className="flex items-start justify-between mb-2">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `${module.color}22`, color: module.color }}
        >
          <Icon className="w-5 h-5" />
        </div>
        {module.launched && (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: colors.ring }} />
            <span className={`text-xs font-bold ${colors.text}`}>{module.score}</span>
          </div>
        )}
        {!module.launched && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-200/50 dark:bg-white/10 opacity-70">
            {t.soon}
          </span>
        )}
      </div>

      <h3 className="font-bold text-sm mb-1">{name}</h3>
      <p className="text-[11px] opacity-70 leading-snug line-clamp-2">{summary}</p>

      {module.launched && (
        <div className="flex items-center gap-1 mt-2 text-xs font-semibold" style={{ color: module.color }}>
          {t.open} <ArrowRight className="w-3.5 h-3.5" />
        </div>
      )}
    </motion.div>
  );

  if (isLink) {
    return <Link to={module.route} data-testid={`hub-link-${module.id}`}>{Inner}</Link>;
  }
  return Inner;
}
